import CarbonFootprintIcon from "@/components/icons/CarbonFootprintIcon";
import {
  BiodiversityIcon,
  FlagIcon,
  HumanHealthIcon,
  ResourceScarcityIcon,
} from "@/components/icons/chapters/MetricGroupIcons";
import { ALL_EF_IMPACTS } from "@/pages/dashboard/utils/EFMetrics";
import { ALL_RECIPE_IMPACTS } from "@/pages/dashboard/utils/recipeMetrics";
import { ReactNode } from "react";
import { formatMassWithUnit } from "./formatters";

export type ImpactCategory = "environmental" | "carbon" | "none";

export type RecipeImpactMetricKey =
  | "environmental_cost"
  | "human_health"
  | "terrestrial_ecosystems"
  | "marine_ecosystems"
  | "freshwater_ecosystems"
  | "ecosystems"
  | "water_use"
  | "toxicity_freshwater"
  | "toxicity_marine"
  | "toxicity_terrestrial"
  | "eutrophication_freshwater"
  | "eutrophication_marine"
  | "acidification"
  | "land_use"
  | "human_toxicity_carcinogenic"
  | "human_toxicity_non_carcinogenic"
  | "ionising_radiation"
  | "ozone_depletion"
  | "particulate_matter"
  | "photochemical_oxidant_human_health"
  | "photochemical_oxidant_terrestrial"
  | "material_resources"
  | "energy_resources"
  | "resource_scarcity";

export type EFImpactMetricKey =
  | "ef_environmental_cost"
  | "ef_acidification"
  | "ef_climate_change_biogenic"
  | "ef_climate_change_fossil"
  | "ef_climate_change_land_use_and_land_use_change"
  | "ef_ecotoxicity_freshwater"
  | "ef_ecotoxicity_freshwater_inorganics"
  | "ef_ecotoxicity_freshwater_organics"
  | "ef_energy_resources_non_renewable"
  | "ef_eutrophication_freshwater"
  | "ef_eutrophication_marine"
  | "ef_eutrophication_terrestrial"
  | "ef_human_toxicity_carcinogenic"
  | "ef_human_toxicity_non_carcinogenic"
  | "ef_ionising_radiation_human_health"
  | "ef_land_use"
  | "ef_material_resources_metals_minerals"
  | "ef_ozone_depletion"
  | "ef_particulate_matter_formation"
  | "ef_photochemical_oxidant_formation_human_health"
  | "ef_water_use";

export type ImpactMetricKey =
  | RecipeImpactMetricKey
  | EFImpactMetricKey
  | "carbon_footprint"
  | "carbon_footprint_scope_1"
  | "carbon_footprint_scope_2"
  | "carbon_footprint_scope_3";

export type ImpactMetricGroupKey =
  | "Main impacts"
  | "Biodiversity"
  | "Human health"
  | "Resource scarcity"
  | "Carbon footprint";

export const ImpactMetricGroupIcons: Record<ImpactMetricGroupKey, ReactNode> = {
  "Main impacts": <FlagIcon />,
  Biodiversity: <BiodiversityIcon />,
  "Human health": <HumanHealthIcon />,
  "Resource scarcity": <ResourceScarcityIcon />,
  "Carbon footprint": <CarbonFootprintIcon />,
};

export interface ImpactMetric {
  key: ImpactMetricKey;
  formatter: (value: number) => string;
  label: string;
  icon?: ReactNode;
  unit: string;
  unitTooltip: string;
  impactGroup?: ImpactMetricGroupKey;
}

export const IMPACT_METRICS: Record<ImpactMetricKey, ImpactMetric> = {
  ...ALL_RECIPE_IMPACTS,
  ...ALL_EF_IMPACTS,
  carbon_footprint: {
    formatter: formatMassWithUnit("CO2-Eq"),
    label: "Carbon footprint (total)",
    key: "carbon_footprint",
    icon: <CarbonFootprintIcon />,
    unit: "CO2-Eq",
    unitTooltip: "Total greenhouse gas emissions contributing to global warming.",
    impactGroup: "Main impacts",
  },
  carbon_footprint_scope_1: {
    formatter: formatMassWithUnit("CO2-Eq"),
    label: "Carbon footprint",
    key: "carbon_footprint_scope_1",
    icon: <CarbonFootprintIcon />,
    unit: "CO2-Eq",
    unitTooltip: "",
  },
  carbon_footprint_scope_2: {
    formatter: formatMassWithUnit("CO2-Eq"),
    label: "Carbon footprint",
    key: "carbon_footprint_scope_2",
    icon: <CarbonFootprintIcon />,
    unit: "CO2-Eq",
    unitTooltip: "",
  },
  carbon_footprint_scope_3: {
    formatter: formatMassWithUnit("CO2-Eq"),
    label: "Carbon footprint",
    key: "carbon_footprint_scope_3",
    icon: <CarbonFootprintIcon />,
    unit: "CO2-Eq",
    unitTooltip: "",
  },
};

export const PREMIUM_IMPACT_METRIC_KEYS: string[] = Object.values(IMPACT_METRICS)
  .filter((metric) => !metric.key.includes("carbon") && !metric.key.includes("environmental"))
  .map((metric) => metric.key);

export type IImpact = Record<ImpactMetricKey, number>;
